import { useEffect, useState } from 'react'

import { isIntercomAvailable, safeIntercom } from './safe'

// The widget script loads asynchronously, so the first render usually
// happens before `window.Intercom` exists. Poll briefly rather than
// registering once and silently missing the handler.
const POLL_INTERVAL_MS = 500

// ...but BOUNDED. The MS Teams build ships no widget at all, so an unbounded
// interval would tick every 500ms for the entire session and never succeed.
// 30s is far longer than the snippet needs on any real connection; past that
// the widget is not coming, and the caller already treats "absent" and
// "nothing unread" alike.
const POLL_TIMEOUT_MS = 30_000
const MAX_POLL_ATTEMPTS = POLL_TIMEOUT_MS / POLL_INTERVAL_MS

/**
 * Live unread-conversation count from Intercom.
 *
 * Replaces a `localStorage.chatClicked` one-shot that lit the nav dot until
 * the button's first click and never again. That flag meant "you have not
 * clicked this yet", so the only attention-drawing element in the top bar
 * went permanently quiet after one accidental click — and an actual reply
 * from support then arrived with no indication at all.
 *
 * Returns 0 when Intercom is absent (the MS Teams build ships without the
 * widget), so callers can treat "no widget" and "nothing unread" alike.
 */
export const useIntercomUnreadCount = (): number => {
  const [unreadCount, setUnreadCount] = useState(0)

  useEffect(() => {
    let intervalId: ReturnType<typeof setInterval> | undefined

    const register = (): boolean => {
      if (!isIntercomAvailable()) return false
      // Intercom keeps ONE handler per event name, so a second consumer of
      // this hook would silently replace the first. Single-consumer by
      // design — mount it in the nav and read the count from there.
      safeIntercom('onUnreadCountChange', (count: unknown) => {
        setUnreadCount(typeof count === 'number' ? count : 0)
      })
      return true
    }

    if (!register()) {
      let attempts = 0
      intervalId = setInterval(() => {
        attempts += 1
        // Stop on success OR on giving up — otherwise the Teams build, where
        // the widget never arrives, keeps a timer alive for the whole session.
        if ((register() || attempts >= MAX_POLL_ATTEMPTS) && intervalId) clearInterval(intervalId)
      }, POLL_INTERVAL_MS)
    }

    return () => {
      if (intervalId) clearInterval(intervalId)
      // Detach so a remount doesn't leave the old setState bound to a
      // torn-down tree. Intercom has no "off", so replace with a no-op.
      safeIntercom('onUnreadCountChange', () => undefined)
    }
  }, [])

  return unreadCount
}

export default useIntercomUnreadCount
